<template>
  <div
    class="timesheet-card-header"
    :class="{ 'timesheet-card-header-expanded': expanded }"
    role="button"
    tabindex="0"
    :aria-expanded="expanded ? 'true' : 'false'"
    @click="$emit('toggle')"
    @keydown.enter.self="$emit('toggle')"
    @keydown.space.self.prevent="$emit('toggle')"
  >
    <div class="timesheet-card-main">
      <div class="timesheet-card-title-row">
        <span class="timesheet-project">{{ ts.project_name }}</span>
        <span v-if="editing" class="editing-pill">
          <b-icon-pencil />
          Editing
        </span>
      </div>
      <div class="timesheet-metadata">
        <span class="timesheet-meta">
          <b-icon-calendar3 aria-hidden="true" />
          {{ formatWeek(ts.week_start) }}
        </span>
        <span class="timesheet-meta">
          <b-icon-person aria-hidden="true" />
          {{ ts.supervisor.full_name }}
        </span>
        <span class="timesheet-meta">
          <b-icon-people aria-hidden="true" />
          {{ workerCount }} worker{{ workerCount !== 1 ? 's' : '' }}
        </span>
        <span v-if="submittedOn" class="timesheet-meta">
          <b-icon-send aria-hidden="true" />
          Submitted {{ submittedOn }}
        </span>
        <span v-if="lateLabel" class="late-pill">{{ lateLabel }}</span>
      </div>
    </div>

    <div class="timesheet-card-status">
      <b-badge
        pill
        :variant="statusVariant(ts.status)"
        class="text-capitalize status-pill"
      >
        {{ ts.status }}
      </b-badge>
      <span class="expand-control" aria-hidden="true">
        <b-icon-chevron-right
          class="expand-icon"
          :class="{ expanded: expanded }"
        />
      </span>
    </div>
  </div>
</template>

<script>
import {
  tokenRows,
  supervisorStatusVariant as statusVariant,
  formatWeekRange as formatWeek,
  formatSubmittedOn,
  formatDaysLate,
} from '@/utils/timesheetHelpers'

export default {
  name: 'SupervisorTimesheetCardHeader',
  props: {
    ts: { type: Object, required: true },
    expanded: { type: Boolean, default: false },
    editing: { type: Boolean, default: false },
  },
  computed: {
    workerCount() {
      return tokenRows(this.ts).length
    },
    submittedOn() {
      return formatSubmittedOn(this.ts.submitted_at)
    },
    lateLabel() {
      return formatDaysLate(this.ts.days_late)
    },
  },
  methods: {
    statusVariant,
    formatWeek,
  },
}
</script>

<style lang="scss" scoped>
.timesheet-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 18px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s;
}

.timesheet-card-header:hover,
.timesheet-card-header-expanded {
  background: #f8fafc;
}

.timesheet-card-header:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: -2px;
}

.timesheet-card-main {
  min-width: 0;
}

.timesheet-card-title-row,
.timesheet-metadata,
.timesheet-meta,
.timesheet-card-status,
.editing-pill,
.expand-control {
  display: flex;
  align-items: center;
}

.timesheet-card-title-row {
  gap: 8px;
  min-width: 0;
}

.timesheet-metadata {
  flex-wrap: wrap;
  gap: 6px 16px;
  margin-top: 5px;
}

.timesheet-project {
  color: #111827;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.timesheet-meta {
  gap: 5px;
  color: #6b7280;
  font-size: 12px;
  white-space: nowrap;
}

.timesheet-meta .b-icon {
  color: #9ca3af;
  font-size: 12px;
}

.editing-pill {
  gap: 4px;
  padding: 2px 7px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.late-pill {
  padding: 1px 8px;
  border: 1px solid #fdba74;
  border-radius: 999px;
  background: #fff7ed;
  color: #c2410c;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.timesheet-card-status {
  gap: 12px;
  flex-shrink: 0;
}

.status-pill {
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
}

.expand-control {
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  background: #fff;
}

.expand-icon {
  color: #9ca3af;
  font-size: 11px;
  transition: transform 0.2s;
}

.expand-icon.expanded {
  transform: rotate(90deg);
}

@media (max-width: 575px) {
  .timesheet-card-header {
    align-items: flex-start;
    gap: 10px;
    padding: 14px;
  }

  .timesheet-card-main {
    flex: 1 1 auto;
  }

  .timesheet-metadata {
    gap: 5px 10px;
  }

  .timesheet-meta {
    font-size: 11px;
  }

  .timesheet-card-status {
    gap: 8px;
  }
}

@media (max-width: 420px) {
  .timesheet-card-header {
    flex-direction: column;
  }

  .timesheet-card-status {
    justify-content: space-between;
    width: 100%;
  }
}
</style>
